import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Coins, Flame, Sparkles, Package, Gift, Boxes, Zap, ChevronRight, ShieldCheck, Bolt, Gem, Globe, Trophy, ArrowRight, Twitter } from 'lucide-react';

export const RARITY = {
  Common: { c: '#94A3B8', g: 'rgba(148,163,184,0.4)' },
  Rare: { c: '#3B82F6', g: 'rgba(59,130,246,0.5)' },
  Epic: { c: '#A855F7', g: 'rgba(168,85,247,0.55)' },
  Legendary: { c: '#EAB308', g: 'rgba(234,179,8,0.6)' },
  Mythic: { c: '#EF4444', g: 'rgba(239,68,68,0.6)' },
};
const RARITY_ORDER = ['Common', 'Rare', 'Epic', 'Legendary', 'Mythic'];
export const priceToRarity = (p) => p >= 1500 ? 'Mythic' : p >= 800 ? 'Legendary' : p >= 200 ? 'Epic' : p >= 50 ? 'Rare' : 'Common';

const RarityBadge = ({ r }) => (
  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border"
    style={{ color: RARITY[r].c, borderColor: RARITY[r].c + '66', background: RARITY[r].c + '1a' }}>{r}</span>
);

const reveal = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-60px' } };

export const SectionTitle = ({ kicker, title, sub }) => (
  <div className="mb-10 text-center lg:text-left">
    {kicker && <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#71DFE7] mb-2">{kicker}</p>}
    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight uppercase text-white font-['Unbounded']">{title}</h2>
    {sub && <p className="text-slate-400 mt-2 max-w-2xl mx-auto lg:mx-0">{sub}</p>}
  </div>
);

/* ---------------- Hero ---------------- */
export const HeroSection = ({ heroImg, packImgs = [], onStart, onExplore }) => {
  const badges = [
    { icon: Package, label: 'TCG Collectibles' },
    { icon: Bolt, label: 'Instant Pulls' },
    { icon: Gem, label: 'Rare Rewards' },
    { icon: Globe, label: 'Web3 Ready' },
  ];
  return (
    <section className="relative overflow-hidden">
      <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#009DAE]/25 blur-[120px] gp-glow" />
      <div className="absolute top-10 right-0 w-[420px] h-[420px] rounded-full bg-[#FFD700]/10 blur-[120px]" />
      <div className="max-w-[1500px] mx-auto px-4 lg:px-8 pt-12 lg:pt-20 pb-10 grid lg:grid-cols-2 gap-10 items-center relative">
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-mono uppercase tracking-widest text-[#71DFE7] mb-5">
            <Sparkles size={13} /> Next-Gen Gacha Platform
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.95] text-white font-['Unbounded']">
            Open Packs.<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#71DFE7] to-[#009DAE]">Win Rare</span> <span className="text-[#FFD700]">Collectibles.</span>
          </h1>
          <p className="text-slate-400 mt-5 text-base sm:text-lg max-w-xl">
            Experience the next generation of collectible gacha with TCG packs, action figures, rewards, and surprise drops.
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <button onClick={onStart} data-testid="hero-start-opening" className="h-12 px-6 rounded-full font-extrabold text-[#040914] bg-gradient-to-r from-[#FFD700] to-[#FFAA00] shadow-[0_0_26px_rgba(255,215,0,0.5)] hover:scale-105 transition-transform flex items-center gap-2">
              <Zap size={18} fill="#040914" /> Start Opening
            </button>
            <button onClick={onExplore} data-testid="hero-explore-packs" className="h-12 px-6 rounded-full font-bold text-white bg-white/5 border border-white/15 hover:border-[#71DFE7]/60 hover:text-[#71DFE7] transition-colors flex items-center gap-2">
              Explore Packs <ArrowRight size={18} />
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-9 max-w-xl">
            {badges.map(({ icon: Ic, label }) => (
              <div key={label} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5">
                <Ic size={16} className="text-[#71DFE7] shrink-0" />
                <span className="text-xs font-semibold text-slate-200">{label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right visual */}
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative h-[360px] sm:h-[440px] lg:h-[520px]">
          <div className="absolute inset-0 rounded-[2rem] overflow-hidden border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_0_60px_-12px_rgba(0,157,174,0.6)]">
            {heroImg && <img src={heroImg} alt="CARDTOPIA gacha machine" className="w-full h-full object-cover opacity-80" />}
            <div className="absolute inset-0 bg-gradient-to-t from-[#040914] via-transparent to-transparent" />
          </div>
          {/* Floating packs */}
          {packImgs[0] && <img src={packImgs[0]} alt="" className="absolute -left-4 top-10 w-24 sm:w-28 drop-shadow-[0_10px_30px_rgba(0,157,174,0.6)] gp-float" />}
          {packImgs[1] && <img src={packImgs[1]} alt="" className="absolute right-2 bottom-8 w-28 sm:w-36 drop-shadow-[0_10px_30px_rgba(255,215,0,0.4)] gp-float-slow" />}
          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/60 backdrop-blur-xl border border-white/10 rounded-full px-4 py-2">
            <Coins size={16} className="text-[#FFD700]" /><span className="text-sm font-bold">Live Drops Active</span>
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- Live Ticker ---------------- */
export const LiveTicker = ({ items }) => {
  const row = [...items, ...items];
  return (
    <div className="relative border-y border-white/10 bg-black/40 py-3 overflow-hidden">
      <div className="gp-marquee-track gap-3">
        {row.map((it, i) => (
          <div key={i} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full pl-2 pr-3 py-1.5 mx-1.5 whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: RARITY[it.rarity].c }} />
            <span className="text-sm text-slate-200"><b className="text-white">{it.user}</b> pulled <b style={{ color: RARITY[it.rarity].c }}>{it.reward}</b></span>
            <RarityBadge r={it.rarity} />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Category Card ---------------- */
const CAT_ICONS = [Package, Boxes, Gift, Zap, Trophy, Sparkles, Gem, Flame];
export const CategoryCard = ({ index, title, desc, count, image, onView }) => {
  const Ic = CAT_ICONS[index % CAT_ICONS.length];
  return (
    <motion.div {...reveal} transition={{ delay: Math.min(index * 0.05, 0.3) }} whileHover={{ y: -8, rotateX: 4, rotateY: -4 }}
      onClick={onView} style={{ transformStyle: 'preserve-3d' }}
      className="group relative rounded-2xl p-5 cursor-pointer bg-gradient-to-b from-white/5 to-transparent border border-white/10 hover:border-[#71DFE7]/50 hover:shadow-[0_0_28px_-6px_rgba(0,157,174,0.6)] transition-all duration-300 overflow-hidden"
      data-testid={`category-card-${index}`}>
      <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-[#009DAE]/20 blur-2xl group-hover:bg-[#009DAE]/40 transition-colors" />
      <div className="relative w-14 h-14 rounded-2xl overflow-hidden mb-4 border border-white/10 bg-[#040914] flex items-center justify-center">
        {image ? <img src={image} alt={title} className="w-full h-full object-cover" /> : <Ic size={24} className="text-[#71DFE7]" />}
      </div>
      <h3 className="text-white font-bold text-base mb-1 line-clamp-1">{title}</h3>
      <p className="text-slate-400 text-xs line-clamp-2 mb-4 min-h-[2rem]">{desc}</p>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-mono text-[#71DFE7]">{count} packs</span>
        <span className="flex items-center gap-1 text-xs font-bold text-white group-hover:text-[#FFD700] transition-colors">View Packs <ChevronRight size={14} /></span>
      </div>
    </motion.div>
  );
};

/* ---------------- Product Card ---------------- */
export const ProductCard = ({ index, title, category, rarity, price, remaining, total, image, trending, onPull }) => {
  const pct = total > 0 ? Math.round((remaining / total) * 100) : 0;
  return (
    <motion.div {...reveal} transition={{ delay: Math.min(index * 0.05, 0.3) }} whileHover={{ y: -8 }}
      className="group relative rounded-2xl bg-gradient-to-b from-white/5 to-transparent border border-white/10 hover:border-[#71DFE7]/50 hover:shadow-[0_0_30px_-8px_rgba(0,157,174,0.7)] overflow-hidden transition-all duration-300"
      data-testid={`product-card-${index}`}>
      <div className="relative aspect-[4/5] overflow-hidden">
        {image && <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#040914] via-[#040914]/30 to-transparent" />
        <div className="absolute top-2 left-2"><RarityBadge r={rarity} /></div>
        {trending && (
          <div className="absolute top-2 right-2 flex items-center gap-1 bg-[#EF4444]/90 rounded-full px-2 py-0.5 text-[10px] font-bold text-white">
            <Flame size={11} className="animate-pulse" /> HOT
          </div>
        )}
        <div className="absolute bottom-2 left-2 right-2">
          <span className="text-[10px] font-mono uppercase tracking-wider text-[#71DFE7]">{category}</span>
          <h4 className="text-white font-bold text-sm leading-tight line-clamp-2">{title}</h4>
        </div>
      </div>
      <div className="p-3 space-y-2.5">
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-400">Stock</span>
          <span className="text-white font-semibold">{remaining}/{total}</span>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[#71DFE7] to-[#009DAE]" style={{ width: `${pct}%` }} />
        </div>
        <button onClick={onPull} data-testid={`pull-now-${index}`} className="w-full h-10 rounded-xl font-extrabold text-[#040914] bg-gradient-to-r from-[#FFD700] to-[#FFAA00] hover:shadow-[0_0_18px_rgba(255,215,0,0.5)] transition-all flex items-center justify-center gap-1.5">
          Pull Now <Coins size={14} className="text-[#040914]" /> <span>{price}</span>
        </button>
      </div>
    </motion.div>
  );
};

/* ---------------- How It Works ---------------- */
export const HowItWorks = () => {
  const steps = [
    { icon: Package, title: 'Choose Your Pack', desc: 'Browse TCG packs, figures, mystery boxes & rewards.' },
    { icon: Sparkles, title: 'Open the Gacha', desc: 'Trigger the animated pull and feel the thrill.' },
    { icon: Gift, title: 'Claim Your Reward', desc: 'Keep, showcase, or cash out your collectibles.' },
  ];
  return (
    <section id="how-it-works" className="max-w-[1500px] mx-auto px-4 lg:px-8 py-16 scroll-mt-20">
      <SectionTitle kicker="Simple & Transparent" title="How It Works" />
      <div className="grid sm:grid-cols-3 gap-6 relative">
        <div className="hidden sm:block absolute top-10 left-[16%] right-[16%] h-px bg-gradient-to-r from-[#71DFE7]/0 via-[#71DFE7]/40 to-[#71DFE7]/0" />
        {steps.map((s, i) => (
          <motion.div key={i} {...reveal} transition={{ delay: i * 0.1 }} className="relative text-center bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#71DFE7]/40 transition-colors">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-[#71DFE7] to-[#009DAE] flex items-center justify-center shadow-[0_0_24px_rgba(0,157,174,0.5)] mb-4 relative z-10">
              <s.icon size={28} className="text-[#040914]" />
            </div>
            <span className="text-[#FFD700] font-mono text-sm">Step {i + 1}</span>
            <h3 className="text-white font-bold text-lg mt-1">{s.title}</h3>
            <p className="text-slate-400 text-sm mt-2">{s.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

/* ---------------- Reward Showcase ---------------- */
export const RewardShowcase = () => (
  <section className="max-w-[1500px] mx-auto px-4 lg:px-8 py-16">
    <SectionTitle kicker="Pull Tiers" title="Reward Showcase" sub="From everyday commons to one-of-a-kind mythics — every pull is a chance at glory." />
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      {RARITY_ORDER.map((r, i) => {
        const premium = r === 'Legendary' || r === 'Mythic';
        return (
          <motion.div key={r} {...reveal} transition={{ delay: i * 0.06 }}
            className={`relative rounded-2xl p-5 text-center overflow-hidden border ${premium ? 'gp-animated-gradient' : 'bg-white/5'} ${i === 4 ? 'col-span-2 lg:col-span-1' : ''}`}
            style={premium ? { borderColor: RARITY[r].c + '88', backgroundImage: `linear-gradient(135deg, ${RARITY[r].c}22, transparent, ${RARITY[r].c}22)`, boxShadow: `0 0 30px -8px ${RARITY[r].g}` } : { borderColor: RARITY[r].c + '33' }}>
            <Gem size={26} className="mx-auto mb-3" style={{ color: RARITY[r].c }} />
            <p className="font-black uppercase tracking-wide text-sm" style={{ color: RARITY[r].c }}>{r}</p>
            <p className="text-slate-400 text-xs mt-1">{premium ? 'Holographic ultra-rare' : 'Standard drop'}</p>
          </motion.div>
        );
      })}
    </div>
  </section>
);

/* ---------------- Winners Feed ---------------- */
export const WinnersFeed = ({ winners }) => (
  <section id="winners" className="max-w-[1500px] mx-auto px-4 lg:px-8 py-16 scroll-mt-20">
    <SectionTitle kicker="Live Activity" title="Recent Winners" />
    <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
      <div className="grid grid-cols-12 px-4 py-3 text-[11px] font-mono uppercase tracking-wider text-slate-400 border-b border-white/10">
        <span className="col-span-3">User</span><span className="col-span-4">Pack</span><span className="col-span-3">Reward</span><span className="col-span-2 text-right">Rarity</span>
      </div>
      {winners.map((w, i) => (
        <motion.div key={i} {...reveal} transition={{ delay: Math.min(i * 0.04, 0.4) }} className="grid grid-cols-12 px-4 py-3 items-center text-sm border-b border-white/5 hover:bg-white/5 transition-colors">
          <span className="col-span-3 flex items-center gap-2 min-w-0">
            <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#009DAE] to-[#007A8A] flex items-center justify-center text-[10px] font-bold shrink-0">{w.user.slice(0, 2).toUpperCase()}</span>
            <span className="text-white font-semibold truncate">{w.user}</span>
          </span>
          <span className="col-span-4 text-slate-300 truncate">{w.pack}</span>
          <span className="col-span-3 font-semibold truncate" style={{ color: RARITY[w.rarity].c }}>{w.reward}</span>
          <span className="col-span-2 text-right"><RarityBadge r={w.rarity} /></span>
        </motion.div>
      ))}
    </div>
  </section>
);

/* ---------------- Community / Web3 ---------------- */
export const CommunitySection = () => (
  <section className="max-w-[1500px] mx-auto px-4 lg:px-8 py-16">
    <div className="relative rounded-3xl border border-white/10 overflow-hidden p-8 lg:p-14 text-center bg-gradient-to-br from-[#009DAE]/15 via-transparent to-[#FFD700]/10">
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-[#009DAE]/20 blur-[120px] gp-glow" />
      <h2 className="relative text-2xl sm:text-3xl lg:text-4xl font-black uppercase text-white font-['Unbounded'] max-w-3xl mx-auto leading-tight">
        Collectibles, Entertainment, Community & <span className="text-[#71DFE7]">Web3 Rewards</span> in One Platform
      </h2>
      <div className="relative flex flex-wrap justify-center gap-3 mt-8">
        <a href="#" data-testid="community-join" className="h-11 px-5 rounded-full font-bold bg-white/5 border border-white/15 hover:border-[#71DFE7]/60 hover:text-[#71DFE7] transition-colors flex items-center gap-2"><Globe size={16} /> Join Community</a>
        <a href="#" data-testid="community-x" className="h-11 px-5 rounded-full font-bold bg-white/5 border border-white/15 hover:border-[#71DFE7]/60 hover:text-[#71DFE7] transition-colors flex items-center gap-2"><Twitter size={16} /> Follow on X</a>
        <a href="#" data-testid="community-token" className="h-11 px-5 rounded-full font-extrabold text-[#040914] bg-gradient-to-r from-[#FFD700] to-[#FFAA00] flex items-center gap-2"><Coins size={16} fill="#040914" /> View Token Info</a>
      </div>
    </div>
  </section>
);

/* ---------------- Final CTA ---------------- */
export const CTASection = ({ onPlay }) => (
  <section className="max-w-[1500px] mx-auto px-4 lg:px-8 py-16">
    <div className="relative rounded-3xl overflow-hidden border border-[#FFD700]/20 p-10 lg:p-16 text-center bg-gradient-to-br from-[#06151f] to-[#04121a]">
      <div className="absolute inset-0 gp-animated-gradient opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 30% 30%, rgba(0,157,174,0.4), transparent 40%), radial-gradient(circle at 70% 70%, rgba(255,215,0,0.25), transparent 40%)' }} />
      <h2 className="relative text-3xl sm:text-4xl lg:text-5xl font-black uppercase text-white font-['Unbounded'] leading-tight">Your Next Rare Pull <span className="text-[#FFD700]">Starts Here.</span></h2>
      <p className="relative text-slate-300 mt-4 max-w-2xl mx-auto">Join CARDTOPIA and discover rare collectibles through a fun, transparent, and rewarding gacha experience.</p>
      <button onClick={onPlay} data-testid="cta-play-now" className="relative mt-8 h-14 px-10 rounded-full font-extrabold text-lg text-[#040914] bg-gradient-to-r from-[#FFD700] to-[#FFAA00] shadow-[0_0_36px_rgba(255,215,0,0.5)] hover:scale-105 transition-transform inline-flex items-center gap-2">
        <Zap size={20} fill="#040914" /> Play CARDTOPIA Now
      </button>
    </div>
  </section>
);
